/**
 * Voice Waveform Component
 *
 * Visualizes audio input from MediaStream using Web Audio API
 * - Real-time frequency analysis with AnalyserNode
 * - Animated bars showing audio levels
 * - Smooth animations with requestAnimationFrame
 */

import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:voice-waveform');
import { cn } from '../utils/cn';

export interface VoiceWaveformProps {
  /**
   * MediaStream from getUserMedia (microphone)
   */
  stream: MediaStream | null;

  /**
   * Number of frequency bars to display
   * @default 5
   */
  barCount?: number;

  /**
   * Height of the waveform container in pixels
   * @default 32
   */
  height?: number;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Voice Waveform Visualization
 *
 * Shows animated bars that respond to microphone input
 */
export const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  stream,
  barCount = 5,
  height = 32,
  className,
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const dataArrayRef = React.useRef<Uint8Array | null>(null);

  React.useEffect(() => {
    if (!stream || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) {
      return;
    }

    // Get color from CSS custom properties (theme-aware, supports dark mode)
    const computedStyle = getComputedStyle(canvas);
    const textSecondaryColor =
      computedStyle.getPropertyValue('--color-text-secondary').trim() || '#36455a'; // Fallback

    // Create AudioContext and AnalyserNode
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();

    // Configure analyser for better voice responsiveness
    analyser.fftSize = 512; // Higher resolution
    analyser.smoothingTimeConstant = 0.3; // More responsive (less smoothing)
    analyser.minDecibels = -90; // Sensitivity range
    analyser.maxDecibels = -10;

    // Connect source to analyser (no output needed)
    source.connect(analyser);

    // Create data array
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Store in refs
    audioContextRef.current = audioCtx;
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    // Animation function
    const draw = () => {
      if (!analyserRef.current || !dataArrayRef.current || !canvasCtx) {
        return;
      }

      animationFrameRef.current = requestAnimationFrame(draw);

      const dataArray = dataArrayRef.current;

      // Get frequency data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      analyserRef.current.getByteFrequencyData(dataArray);

      // Clear canvas
      canvasCtx.fillStyle = 'transparent';
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate bar dimensions
      const barWidth = canvas.width / barCount;
      const gap = 1; // Gap between bars (1px for compact look)
      const effectiveBarWidth = barWidth - gap;

      // Focus on lower frequencies (better for voice: 100-1000Hz range)
      // Instead of sampling entire spectrum, focus on first ~40% (voice range)
      const voiceRangeEnd = Math.floor(bufferLength * 0.4);
      const samplesPerBar = Math.floor(voiceRangeEnd / barCount);

      for (let i = 0; i < barCount; i++) {
        // Average the frequency data for this bar
        let sum = 0;
        for (let j = 0; j < samplesPerBar; j++) {
          const index = i * samplesPerBar + j;
          sum += dataArray[index] || 0;
        }
        const average = sum / samplesPerBar;

        // Normalize to 0-1 range and amplify for more visible movement
        let normalizedValue = average / 255;
        normalizedValue = Math.pow(normalizedValue, 0.7); // Power curve for better visibility

        // Calculate bar height with smaller minimum for more dynamic range
        const minHeight = 0.1;
        const barHeight = canvas.height * (minHeight + normalizedValue * (1 - minHeight));

        // Calculate position (centered vertically)
        const x = i * barWidth;
        const y = (canvas.height - barHeight) / 2;

        // Use theme color (from CSS custom property)
        canvasCtx.fillStyle = textSecondaryColor;

        // Draw rounded rectangle using standard Canvas API (compatible with all Electron versions)
        const radius = Math.min(effectiveBarWidth / 2, 2, barHeight / 2);
        canvasCtx.beginPath();
        canvasCtx.moveTo(x + radius, y);
        canvasCtx.lineTo(x + effectiveBarWidth - radius, y);
        canvasCtx.arcTo(x + effectiveBarWidth, y, x + effectiveBarWidth, y + radius, radius);
        canvasCtx.lineTo(x + effectiveBarWidth, y + barHeight - radius);
        canvasCtx.arcTo(
          x + effectiveBarWidth,
          y + barHeight,
          x + effectiveBarWidth - radius,
          y + barHeight,
          radius
        );
        canvasCtx.lineTo(x + radius, y + barHeight);
        canvasCtx.arcTo(x, y + barHeight, x, y + barHeight - radius, radius);
        canvasCtx.lineTo(x, y + radius);
        canvasCtx.arcTo(x, y, x + radius, y, radius);
        canvasCtx.closePath();
        canvasCtx.fill();
      }
    };

    // Start animation
    logger.info('🎵 [VoiceWaveform] Starting animation with', barCount, 'bars');
    draw();

    // Cleanup function
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      analyserRef.current = null;
      dataArrayRef.current = null;
    };
  }, [stream, barCount, height]);

  if (!stream) {
    return null;
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <canvas
        ref={canvasRef}
        width={barCount * 4} // 4px per bar (3px bar + 1px gap) - more compact for 20 bars
        height={height}
        className="rounded"
        style={{
          width: `${barCount * 4}px`,
          height: `${height}px`,
        }}
      />
    </div>
  );
};
